import { AxiosResponse } from 'axios';

import { useQueryClient } from '@tanstack/react-query';

import { registerMockAPI } from '~/mocks/mockApis';
import { TypeRequestHeaders, axiosInstance } from '~/services/fetchServiceV2';
import type {
  TypeCodeState,
  TypeContract,
  TypeSafetyEducation,
  TypeSpecialMedicalExamination,
  TypeUserSubscribe,
} from '~/types';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

import type { TypePrivacyInfo } from './useAgreeContractTerms';
import { useTokenStore } from '~/store';

/** @category Login */
export type TypeUserInfoResponse = {
  code: string;
  message: string;
  data: {
    /** 계정 코드 */
    laborAccountId: number;
    /** 로그인 아이디 */
    loginId: string;
    /** 이름 */
    name: string;
    /** 작업자 계정 상태 */
    status: TypeCodeState;
    /** 계약 구분 */
    contractType: TypeContract;
    /** 휴대전화 번호 */
    phoneNumber: string;
    /** 상용직 정보 */
    kurlyContract: {
      /** 클러스터 */
      cluster: TypeCodeState;
      /** 센터 */
      center: TypeCodeState;
      /** 업무 파트 */
      workPart: TypeCodeState;
    } | null;
    /** 최근 출근 정보 */
    latestCommute: {
      /** 클러스터 */
      cluster: TypeCodeState;
      /** 센터 */
      center: TypeCodeState;
    } | null;
    /** 계약 시 입력한 개인정보 */
    privacyInfo: TypePrivacyInfo | null;
    /** 근로 계약 가능 여부 */
    contractAvailableStatus: TypeCodeState | null;
    /** 근로 계약 일시  */
    latestContractSignedAt: string | null;
    /** 작업자 수신동의 */
    subscribe: TypeUserSubscribe | null;
    /** 현재 근로계약상태 */
    currentContractStatus: TypeCodeState | null;
    /** 본인인증 상태 */
    certificationStatus: TypeCodeState | null;
    /** 신규안전교육 상태 */
    safetyEducation: TypeSafetyEducation;
    /** 초기 비밀번호 사용 여부 */
    initPassword: boolean;
    /** 비밀번호 변경 필요 여부 */
    passwordChangeRequired: boolean;
    /** 특수건강검진 정보 */
    specialMedicalExamination: TypeSpecialMedicalExamination | null;
    /** 재동의 필요 여부 */
    reConsentRequired: boolean;
  };
};

/**
 * @description 계정정보 조회 api 요청에 사용되는 커스텀 훅
 */
export const useUserInfo = () => {
  const queryClient = useQueryClient();

  const handleSuccess = (data: TypeUserInfoResponse) => {
    return data?.data ?? null;
  };

  const requestUserInfo = async ({
    onSuccess,
    onError,
    onFinally,
    headers,
    showLoading = true,
  }: {
    onSuccess?: (data: TypeUserInfoResponse['data']) => void;
    onError?: (error: TypeErrorFormat) => void;
    onFinally?: () => void;
    headers?: TypeRequestHeaders;
    showLoading?: boolean;
  }) => {
    const endpoint = '/labor-account';
    const query = queryClient.ensureQueryData<AxiosResponse<TypeUserInfoResponse>>({
      queryKey: ['labor-account'],
      queryFn: () => axiosInstance.get(endpoint, { headers }),
      gcTime: 0,
    });

    return requestHelper(
      () => query,
      (response) => onSuccess?.(handleSuccess(response)),
      onError,
      onFinally,
      { showLoading }
    );
  };

  return requestUserInfo;
};

registerMockAPI<TypeUserInfoResponse>({
  method: 'get',
  endpoint: '/labor-account',
  label: '근무 기본 정보/계정 정보 조회',
  scenarios: [
    {
      name: '200 / 일용직',
      response: {
        code: '0000',
        message: '성공',
        data: {
          laborAccountId: 577,
          loginId: 'kurlyro12',
          initPassword: false,
          passwordChangeRequired: false,
          name: '김컬리로',
          status: { code: 'NORMAL', text: '정상' },
          contractType: 'SHORT',
          phoneNumber: '01012345678',
          safetyEducation: {
            status: { code: 'REQUIRED_REFRESHER', text: '갱신필요' },
            completeDateTime: '2025-10-15T17:52:09.938914',
            nextSafetyEducationDate: '2026-04-15',
          },
          kurlyWorksId: '0MJJZQ0F9PRZWkurlyro',
          kurlyContract: null,
          latestCommute: {
            cluster: { code: 'CC02', text: '김포' },
            center: { code: 'GGL1', text: '김포냉동' },
            workPart: { code: 'IB', text: 'IB' },
            contractType: { code: 'SHORT', text: '아르바이트' },
            workDate: '2026-06-24',
          },
          specialMedicalExamination: {
            id: 158,
            status: { code: 'SECOND_APPROVED', text: '2차 승인' },
            submissionStatus: { code: 'UNAVAILABLE', text: '이용불가' },
            secondExaminationInProgress: false,
            secondExaminationContactPoint: null,
            counselingRequired: false,
            counselingLink: null,
          },
          privacyInfo: {
            juminNo: '980131-2******',
            maskedJuminNo: '980131-2******',
            postCode: '06253',
            address: '서울 강남구 강남대로 298',
            detailAddress: '',
            financialInstitution: { code: '089', text: '케이뱅크' },
            accountNo: '100213159562',
            maskedAccountNo: '1002********',
            accountName: '김컬리로',
          },
          latestContractId: 340,
          latestContractSignedAt: '2026-06-24T13:07:24.861152',
          contractAvailableStatus: { code: 'ENABLE', text: '가능' },
          certificationStatus: { code: 'COMPLETED_BY_ADMIN', text: '인증: 관리자처리' },
          subscribe: {
            allowRecruit: true,
            allowedRecruitAt: '2025-12-30T11:02:01.181925',
            allowNightTime: true,
            allowedNightTimeAt: '2025-12-30T11:02:01.18193',
          },
          reConsentRequired: false,
          requireSafetyEducation: true,
          currentContractStatus: { code: 'RENEWAL', text: '재계약' },
        },
      } as unknown as TypeUserInfoResponse,
    },
    {
      name: '200 / 상용직',
      response: {
        code: '0000',
        message: '성공',
        data: {
          laborAccountId: 484,
          loginId: 'kurlyro',
          initPassword: false,
          passwordChangeRequired: true,
          name: '김컬리로',
          status: { code: 'NORMAL', text: '정상' },
          contractType: 'CONTRACT',
          phoneNumber: '01012345678',
          safetyEducation: {
            status: { code: 'REQUIRED', text: '교육필요' },
            completeDateTime: null,
            nextSafetyEducationDate: null,
          },
          kurlyWorksId: null,
          kurlyContract: {
            cluster: { code: 'CC03', text: '평택' },
            center: { code: 'GPH1', text: '평택상온' },
            workPart: { code: 'OB', text: 'OB' },
            contractedAt: '2024-10-04T18:36:50.544699',
            contractedDate: '2024-10-04',
          },
          latestCommute: {
            cluster: { code: 'CC03', text: '평택' },
            center: { code: 'GPH1', text: '평택상온' },
            workPart: { code: 'OB', text: 'OB' },
            contractType: { code: 'CONTRACT', text: '상용직' },
            workDate: '2026-04-07',
          },
          specialMedicalExamination: {
            id: 151,
            status: { code: 'APPROVED', text: '승인' },
            submissionStatus: { code: 'REQUIRED', text: '제출필요' },
            secondExaminationInProgress: false,
            secondExaminationContactPoint: null,
            counselingRequired: true,
            counselingLink: 'https://www.google.com/search?q=%ED%8F%89%ED%83%9D',
          },
          privacyInfo: {
            juminNo: null,
            maskedJuminNo: null,
            postCode: null,
            address: null,
            detailAddress: null,
            financialInstitution: null,
            accountNo: null,
            maskedAccountNo: null,
            accountName: null,
          },
          latestContractId: null,
          latestContractSignedAt: null,
          contractAvailableStatus: null,
          certificationStatus: null,
          subscribe: {
            allowRecruit: true,
            allowedRecruitAt: '2025-12-31T18:12:55.301539',
            allowNightTime: true,
            allowedNightTimeAt: '2025-12-31T18:12:55.301542',
          },
          reConsentRequired: false,
          requireSafetyEducation: false,
          currentContractStatus: { code: 'FIRST', text: '첫 근로계약' },
        },
      } as unknown as TypeUserInfoResponse,
    },
    {
      name: '401 / 인증 실패',
      status: 401,
      preCondition: {
        action: () => undefined,
        cleanUp: () => {
          useTokenStore.getState().actions.setRefreshToken('');
        }
      },
      response: {
        code: 'AUTH_MEMBER',
        message: '인증에 실패했습니다.',
        data: null,
      } as unknown as TypeUserInfoResponse,
    },
    {
      name: '500 / 서버 에러',
      status: 500,
      response: {
        code: '500',
        message: '서버 내부 오류가 발생했습니다.',
        data: null,
      } as unknown as TypeUserInfoResponse,
    },
    {
      name: '타임아웃',
      status: undefined,
      delay: 11000,
      response: {
        code: 'ECONNABORTED',
        message: '요청 시간이 초과되었어요. 다시 시도해주세요.',
        data: null,
      } as unknown as TypeUserInfoResponse,
    },
  ],
});
